import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, MapPin, Bell, Shield, Key, Globe, Moon, DollarSign, X, ChevronDown, Calendar, UserCircle, Camera, Expand, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getResourceByAuthId, Resource } from '../lib/resources';
import { format, parseISO, isValid, startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears, subDays, addDays, setDate, addMonths } from 'date-fns';
import { getPayrollSettings, type PayrollSettings } from '../lib/payrollSettings';
import { useBusiness } from '../contexts/BusinessContext';

// Helper function to format duration
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Helper function to safely format dates
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

// Add getCurrentPeriodRange function that will be updated with actual settings
const getCurrentPeriodRange = (
  frequency = 'monthly',
  startDate = format(new Date(), 'yyyy-MM-dd'),
  customPeriod1Start?: string,
  customPeriod1End?: string,
  customPeriod2Start?: string,
  customPeriod2End?: string
) => {
  const today = new Date();
  const currentDay = today.getDate();
  
  switch (frequency) {
    case 'weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentWeekOffset = Math.floor(daysSinceStart / 7) * 7;
      const currentPeriodStart = addDays(periodStart, currentWeekOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 6);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'bi-weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentPeriodOffset = Math.floor(daysSinceStart / 14) * 14;
      const currentPeriodStart = addDays(periodStart, currentPeriodOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 13);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'semi-monthly': {
      if (currentDay <= 15) {
        return {
          start: setDate(startOfMonth(today), 1),
          end: setDate(startOfMonth(today), 15)
        };
      } else {
        return {
          start: setDate(startOfMonth(today), 16),
          end: endOfMonth(today)
        };
      }
    }
    
    case 'monthly': {
      const periodStart = parseISO(startDate);
      const startDay = periodStart.getDate();
      let currentPeriodStart: Date;
      
      if (currentDay >= startDay) {
        currentPeriodStart = setDate(today, startDay);
      } else {
        currentPeriodStart = setDate(subMonths(today, 1), startDay);
      }
      
      const currentPeriodEnd = addDays(setDate(addDays(currentPeriodStart, 32), startDay), -1);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'custom': {
      if (!customPeriod1Start || !customPeriod1End || !customPeriod2Start || !customPeriod2End) {
        return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
      }
      
      const p1Start = parseInt(customPeriod1Start);
      const p1End = parseInt(customPeriod1End);
      const p2Start = parseInt(customPeriod2Start);
      const p2End = parseInt(customPeriod2End);
      
      // Handle period crossing month boundary (e.g., 26th-10th)
      if (p1Start > p1End) {
        if (currentDay >= p1Start || currentDay <= p1End) {
          const periodStart = currentDay >= p1Start 
            ? setDate(today, p1Start)
            : setDate(subMonths(today, 1), p1Start);
          const periodEnd = currentDay >= p1Start
            ? setDate(addMonths(today, 1), p1End)
            : setDate(today, p1End);
          return { start: periodStart, end: periodEnd };
        }
      } else if (currentDay >= p1Start && currentDay <= p1End) {
        return {
          start: setDate(today, p1Start),
          end: setDate(today, p1End)
        };
      }
      
      if (p2Start > p2End) {
        if (currentDay >= p2Start || currentDay <= p2End) {
          const periodStart = currentDay >= p2Start
            ? setDate(today, p2Start)
            : setDate(subMonths(today, 1), p2Start);
          const periodEnd = currentDay >= p2Start
            ? setDate(addMonths(today, 1), p2End)
            : setDate(today, p2End);
          return { start: periodStart, end: periodEnd };
        }
      } else if (currentDay >= p2Start && currentDay <= p2End) {
        return {
          start: setDate(today, p2Start),
          end: setDate(today, p2End)
        };
      }
      
      // Default fallback
      return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
    }
    
    default:
      return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
  }
};



// Date ranges from ManagePayroll
const DATE_RANGES = [
  { label: 'Current Period', value: 'current_period', getRange: () => {
    return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
  }},
  { label: 'Today', value: 'today', getRange: () => {
    const today = new Date();
    return { start: startOfDay(today), end: endOfDay(today) };
  }},
  { label: 'Yesterday', value: 'yesterday', getRange: () => {
    const yesterday = subDays(new Date(), 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  }},
  { label: 'Select Day', value: 'select_day', getRange: (selectedDay?: Date) => {
    const day = selectedDay || new Date();
    return { start: startOfDay(day), end: endOfDay(day) };
  }},
  { label: 'Last 30 days', value: 'last_30_days', getRange: () => ({
    start: subDays(new Date(), 29),
    end: new Date()
  })},
  { label: 'This Month', value: 'this_month', getRange: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  })},
  { label: 'Last Month', value: 'last_month', getRange: () => {
    const lastMonth = subMonths(new Date(), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  }},
  { label: 'This Year', value: 'this_year', getRange: () => ({
    start: startOfYear(new Date()),
    end: endOfYear(new Date())
  })},
  { label: 'Last Year', value: 'last_year', getRange: () => {
    const lastYear = subYears(new Date(), 1);
    return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
  }},
  { label: 'Custom Range', value: 'custom_range', getRange: (customStartDate?: Date, customEndDate?: Date) => ({
    start: customStartDate || startOfMonth(new Date()),
    end: customEndDate || endOfMonth(new Date())
  })}
];

interface Booking {
  id: string;
  booking_number: number;
  title: string;
  end_date: string;
  completed_at: string;
  booking_service?: BookingService[];
}

interface BookingService {
  name: string;
  description: string | null;
}

interface PayrollEntry {
  id: string;
  resource_id: string;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status: 'PAID' | 'UNPAID';
  completed_at?: string;
}

interface TimeclockEntry {
  id: string;
  clock_in: string | null;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  total_minutes: number | null;
  hourly_rate: number | null;
  process_status: string;
}

interface BookingItem {
  id: string;
  item_name: string;
  item_description: string | null;
  hours: number;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  completed_at: string;
  created_at: string;
  process_status: string;
}

interface BookingData {
  booking_number: number;
  booking_title: string;
  start_date: string;
  end_date: string;
  items: BookingItem[];
  totalHours: number;
  totalEarnings: number;
}

interface PayrollData {
  timeclock: {
    entries: TimeclockEntry[];
    totalHours: number;
    totalEarnings: number;
  };
  bookings: Record<string, BookingData>;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  role?: string;
  resourceId?: string;
  avatarUrl?: string;
}

export default function Profile() {
  const { selectedBusiness } = useBusiness();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(false);
  const [selectedRange, setSelectedRange] = useState(DATE_RANGES.find(r => r.value === 'current_period')!);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCustomRangePickerOpen, setIsCustomRangePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState(selectedRange.getRange());
  const [isEarningsExpanded, setIsEarningsExpanded] = useState(false);
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [earningsData, setEarningsData] = useState({
    totalHours: 0,
    totalEarnings: 0,
    flatRateEntries: [] as PayrollEntry[],
    timeclockEntries: [] as PayrollEntry[],
    commissionEntries: [] as PayrollEntry[]
  });
  // Add payroll settings state
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);

  // Load payroll settings when component mounts
  useEffect(() => {
    const loadPayrollSettings = async () => {
      if (!selectedBusiness?.id) return;
      
      try {
        const settings = await getPayrollSettings(selectedBusiness.id);
        if (settings) {
          console.log('Loaded payroll settings:', settings);
          setPayrollSettings(settings);
          
          // Update the DATE_RANGES array with actual calculated periods
          DATE_RANGES[0].getRange = () => getCurrentPeriodRange(
            settings.frequency,
            settings.start_date,
            settings.custom_period_1_start,
            settings.custom_period_1_end,
            settings.custom_period_2_start,
            settings.custom_period_2_end
          );
          
          // If the currently selected range is current period, update it
          if (selectedRange.value === 'current_period') {
            const updatedRange = DATE_RANGES.find(r => r.value === selectedRange.value)!;
            setDateRange(updatedRange.getRange());
          }
        }
      } catch (error) {
        console.error('Error loading payroll settings:', error);
      }
    };
    
    loadPayrollSettings();
  }, [selectedBusiness?.id]);

  // Update date range when selection changes
  useEffect(() => {
    if (selectedRange.value === 'current_period') {
      if (payrollSettings) {
        const range = getCurrentPeriodRange(
          payrollSettings.frequency,
          payrollSettings.start_date,
          payrollSettings.custom_period_1_start,
          payrollSettings.custom_period_1_end,
          payrollSettings.custom_period_2_start,
          payrollSettings.custom_period_2_end
        );
        setDateRange(range);
      } else {
        setDateRange(getCurrentPeriodRange());
      }
    } else if (selectedRange.value === 'select_day') {
      setDateRange(selectedRange.getRange(selectedDay || new Date()));
    } else if (selectedRange.value === 'custom_range') {
      setDateRange(selectedRange.getRange(customStartDate || undefined, customEndDate || undefined));
    } else {
      setDateRange(selectedRange.getRange());
    }
  }, [selectedRange, selectedDay, customStartDate, customEndDate, payrollSettings]);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const { data: { session } } = await supabase!.auth.getSession();
        if (!session?.user) return;

        // Check if user is admin (business owner)
        const { data: businessData } = await supabase!
          .from('businesses')
          .select('id')
          .eq('owner_id', session.user.id)
          .maybeSingle();

        if (businessData) {
          setUserInfo({
            firstName: 'ADMIN',
            lastName: '',
            email: session.user.email || '',
            isAdmin: true
          });
          return;
        }

        // If not admin, get resource information with new permission role
        const { data: resourceData, error: resourceError } = await supabase!
          .from('resources')
          .select(`
            *,
            role:resource_roles(
              id,
              name,
              description
            )
          `)
          .eq('auth_user_id', session.user.id)
          .single();

        if (resourceError) {
          console.error('Error getting resource:', resourceError);
          return;
        }

        if (resourceData) {
          setUserInfo({
            firstName: resourceData.first_name || '',
            lastName: resourceData.last_name || '',
            email: resourceData.email || '',
            isAdmin: false,
            role: resourceData.role?.name || resourceData.role || 'Resource',
            resourceId: resourceData.id,
            avatarUrl: resourceData.avatar_url
          });
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };

    loadUserInfo();
  }, []);

  useEffect(() => {
    if (userInfo?.resourceId) {
      fetchEarningsData();
    }
  }, [dateRange, userInfo?.resourceId]);

  const fetchEarningsData = async () => {
    if (!userInfo?.resourceId) return;
    
    setIsLoadingEarnings(true);
    try {
      // Convert to ISO string for database query
      const startDate = dateRange.start.toISOString();
      const endDate = dateRange.end.toISOString();

      console.log('Date Range for earnings query:', {
        label: selectedRange.label,
        startDate,
        endDate,
        startDateFormatted: format(dateRange.start, 'yyyy-MM-dd'),
        endDateFormatted: format(dateRange.end, 'yyyy-MM-dd'),
        rangeType: selectedRange.value
      });

      // Fetch flat rate entries with full details
      // Update the query to be more inclusive of entries within the period
      const flatRateQuery = supabase!
        .from('payroll_page')
        .select('*')
        .eq('resource_id', userInfo.resourceId)
        .eq('entry_type', 'flat_rate')
        .or(`completed_at.gte.${startDate},completed_at.lte.${endDate},created_at.gte.${startDate},created_at.lte.${endDate}`)
        .order('completed_at', { ascending: false });

      const { data: flatRateData, error: flatRateError } = await flatRateQuery;

      if (flatRateError) throw flatRateError;

      // Filter flat rate entries to make sure they're within our date range
      const filteredFlatRateData = flatRateData?.filter(entry => {
        const completedDate = entry.completed_at ? new Date(entry.completed_at) : new Date(entry.created_at);
        
        // Log each entry date being considered
        console.log('Flat Rate Entry Date Check:', {
          id: entry.id,
          date: format(completedDate, 'yyyy-MM-dd'),
          withinRange: completedDate >= dateRange.start && completedDate <= dateRange.end,
          startDateMatch: format(completedDate, 'yyyy-MM-dd') === format(dateRange.start, 'yyyy-MM-dd'),
          startDate: format(dateRange.start, 'yyyy-MM-dd'),
          endDate: format(dateRange.end, 'yyyy-MM-dd')
        });
        
        // Add special handling for entries on the period start date - potential timezone issues
        if (format(completedDate, 'yyyy-MM-dd') === format(dateRange.start, 'yyyy-MM-dd')) {
          return true;
        }
        
        // Entry date is within our period range or exactly on the range boundaries
        return (completedDate >= dateRange.start && completedDate <= dateRange.end);
      });

      // Fetch timeclock entries with full details
      // Update the query to ensure we get all entries within the period
      // We need to include entries that:
      // 1. Started within the period (clock_in is between start and end dates)
      // 2. OR ended within the period (clock_out is between start and end dates)
      // 3. OR span across the period (clock_in <= start date AND clock_out >= end date)
      const timeclockQuery = supabase!
        .from('payroll_page')
        .select('*')
        .eq('resource_id', userInfo.resourceId)
        .eq('entry_type', 'timeclock')
        .or(`clock_in.gte.${startDate},clock_in.lte.${endDate},clock_out.gte.${startDate},clock_out.lte.${endDate}`)
        .order('clock_in', { ascending: false });

      const { data: timeclockData, error: timeclockError } = await timeclockQuery;

      if (timeclockError) throw timeclockError;
      
      // Filter out entries that are completely outside our date range
      const filteredTimeclockData = timeclockData?.filter(entry => {
        const clockIn = new Date(entry.clock_in);
        const clockOut = entry.clock_out ? new Date(entry.clock_out) : new Date();
        
        // Entry starts before period ends AND entry ends after period starts
        return clockIn <= dateRange.end && clockOut >= dateRange.start;
      });

      // Fetch commission entries with full details
      const commissionQuery = supabase!
        .from('payroll_page')
        .select('*')
        .eq('resource_id', userInfo.resourceId)
        .eq('entry_type', 'commission')
        .or(`completed_at.gte.${startDate},completed_at.lte.${endDate},created_at.gte.${startDate},created_at.lte.${endDate}`)
        .order('completed_at', { ascending: false });

      const { data: commissionData, error: commissionError } = await commissionQuery;

      if (commissionError) throw commissionError;

      // Filter commission entries to make sure they're within our date range
      const filteredCommissionData = commissionData?.filter(entry => {
        const completedDate = entry.completed_at ? new Date(entry.completed_at) : new Date(entry.created_at);
        
        // Add special handling for entries on the period start date - potential timezone issues
        if (format(completedDate, 'yyyy-MM-dd') === format(dateRange.start, 'yyyy-MM-dd')) {
          return true;
        }
        
        // Entry date is within our period range or exactly on the range boundaries
        return (completedDate >= dateRange.start && completedDate <= dateRange.end);
      });

      console.log('Fetched Earnings Data:', {
        flatRateCount: flatRateData?.length || 0, 
        filteredFlatRateCount: filteredFlatRateData?.length || 0,
        timeclockCount: timeclockData?.length || 0,
        filteredTimeclockCount: filteredTimeclockData?.length || 0,
        commissionCount: commissionData?.length || 0,
        filteredCommissionCount: filteredCommissionData?.length || 0,
        dateRange: `${format(dateRange.start, 'yyyy-MM-dd')} to ${format(dateRange.end, 'yyyy-MM-dd')}`
      });

      // Transform the data to match the PayrollEntry interface
      const transformedFlatRateEntries = (filteredFlatRateData || []).map(entry => ({
        ...entry,
        booking_id: entry.booking_id || null,
        booking_number: entry.booking_number || null,
        booking_title: entry.booking_title || null,
        item_name: entry.item_name || null,
        item_description: entry.item_description || null,
        hours: Number(entry.hours) || 0,
        earnings: Number(entry.total_earnings) || 0,
        process_status: entry.process_status || 'UNPAID'
      }));

      const transformedTimeclockEntries = (filteredTimeclockData || []).map(entry => ({
        ...entry,
        booking_id: entry.booking_id || null,
        booking_number: entry.booking_number || null,
        booking_title: entry.booking_title || null,
        item_name: entry.item_name || null,
        item_description: entry.item_description || null,
        hours: Number(entry.hours) || 0,
        earnings: Number(entry.total_earnings) || 0,
        process_status: entry.process_status || 'UNPAID'
      }));

      const transformedCommissionEntries = (filteredCommissionData || []).map(entry => ({
        ...entry,
        booking_id: entry.booking_id || null,
        booking_number: entry.booking_number || null,
        booking_title: entry.booking_title || null,
        item_name: entry.item_name || 'Commission',
        item_description: entry.item_description || null,
        hours: 0, // Commission entries don't have hours
        earnings: Number(entry.total_earnings) || 0,
        process_status: entry.process_status || 'UNPAID'
      }));

      // Calculate totals
      const flatRateHours = transformedFlatRateEntries.reduce((sum, entry) => sum + entry.hours, 0);
      const flatRateEarnings = transformedFlatRateEntries.reduce((sum, entry) => sum + entry.earnings, 0);
      
      const timeclockHours = transformedTimeclockEntries.reduce((sum, entry) => sum + entry.hours, 0);
      const timeclockEarnings = transformedTimeclockEntries.reduce((sum, entry) => sum + entry.earnings, 0);

      const commissionEarnings = transformedCommissionEntries.reduce((sum, entry) => sum + entry.earnings, 0);

      setEarningsData({
        totalHours: flatRateHours + timeclockHours,
        totalEarnings: flatRateEarnings + timeclockEarnings + commissionEarnings,
        flatRateEntries: transformedFlatRateEntries,
        timeclockEntries: transformedTimeclockEntries,
        commissionEntries: transformedCommissionEntries
      });
    } catch (error) {
      console.error('Error fetching earnings data:', error);
    } finally {
      setIsLoadingEarnings(false);
    }
  };

  // Helper function to format role text
  const formatRole = (role: string) => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Add the handleProfilePhotoUpload function
  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase || !userInfo?.resourceId) return;

    try {
      setIsUploading(true);

      // Upload the file to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userInfo.resourceId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update the resource's avatar_url
      const { error: updateError } = await supabase
        .from('resources')
        .update({ avatar_url: publicUrl })
        .eq('id', userInfo.resourceId);

      if (updateError) throw updateError;

      // Update local state
      setUserInfo(prev => prev ? { ...prev, avatarUrl: publicUrl } : null);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const toggleBooking = (bookingId: string) => {
    setExpandedBookings(prev => {
      const next = new Set(prev);
      if (next.has(bookingId)) {
        next.delete(bookingId);
      } else {
        next.add(bookingId);
      }
      return next;
    });
  };

  // Group flat rate entries by booking and sort entries by date
  const bookings = (earningsData.flatRateEntries || [])
    .sort((a, b) => {
      const dateA = new Date(a.completed_at || a.created_at);
      const dateB = new Date(b.completed_at || b.created_at);
      return dateB.getTime() - dateA.getTime(); // Newest first
    })
    .reduce<Record<string, PayrollEntry[]>>((acc, entry) => {
      if (entry.booking_id) {
        if (!acc[entry.booking_id]) {
          acc[entry.booking_id] = [];
        }
        acc[entry.booking_id].push(entry);
      }
      return acc;
    }, {});

  // Group commission entries by booking and sort entries by date
  const commissionBookings = (earningsData.commissionEntries || [])
    .sort((a, b) => {
      const dateA = new Date(a.completed_at || a.created_at);
      const dateB = new Date(b.completed_at || b.created_at);
      return dateB.getTime() - dateA.getTime(); // Newest first
    })
    .reduce<Record<string, PayrollEntry[]>>((acc, entry) => {
      if (entry.booking_id) {
        if (!acc[entry.booking_id]) {
          acc[entry.booking_id] = [];
        }
        acc[entry.booking_id].push(entry);
      }
      return acc;
    }, {});

  // Debug commission data
  console.log('Commission Debug:', {
    commissionEntries: earningsData.commissionEntries,
    commissionBookings,
    commissionBookingIds: Object.keys(commissionBookings),
    hasCommissionEntries: (earningsData.commissionEntries || []).length > 0
  });

  // Sort timeclock entries by date
  const sortedTimeclockEntries = [...(earningsData.timeclockEntries || [])]
    .sort((a, b) => {
      const dateA = new Date(a.clock_in || a.created_at);
      const dateB = new Date(b.clock_in || b.created_at);
      return dateB.getTime() - dateA.getTime(); // Newest first
    });

  // Sort bookings by their most recent entry date
  const sortedBookingIds = Object.entries(bookings)
    .map(([bookingId, entries]) => {
      const mostRecentDate = entries.reduce((latest, entry) => {
        const entryDate = new Date(entry.completed_at || entry.created_at);
        return entryDate > latest ? entryDate : latest;
      }, new Date(0));
      
      return { bookingId, date: mostRecentDate };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime()) // Newest first
    .map(item => item.bookingId);

  // Sort commission bookings by their most recent entry date
  const sortedCommissionBookingIds = Object.entries(commissionBookings)
    .map(([bookingId, entries]) => {
      const mostRecentDate = entries.reduce((latest, entry) => {
        const entryDate = new Date(entry.completed_at || entry.created_at);
        return entryDate > latest ? entryDate : latest;
      }, new Date(0));
      
      return { bookingId, date: mostRecentDate };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime()) // Newest first
    .map(item => item.bookingId);

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            {/* Profile Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center">
                <label className="relative cursor-pointer group">
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {userInfo.avatarUrl ? (
                      <img 
                        src={userInfo.avatarUrl} 
                        alt="Profile" 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <User className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-5 w-5 text-white" />
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleProfilePhotoUpload}
                    disabled={isUploading}
                  />
                </label>

                <div className="ml-4">
                  <div className="text-lg font-medium text-gray-900">
                    {userInfo?.isAdmin ? 'ADMIN' : `${userInfo?.firstName} ${userInfo?.lastName}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {userInfo?.isAdmin ? 'Administrator' : userInfo?.role || 'Resource'}
                  </div>
                  <div className="text-sm text-gray-500">{userInfo?.email}</div>
                </div>
              </div>
            </div>

            {/* Earnings Report */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Earnings</h3>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    {selectedRange.label}
                    {selectedRange.value === 'custom_range' && customStartDate && customEndDate && (
                      <span className="ml-1 text-gray-500">
                        ({format(customStartDate, 'MMM d')} - {format(customEndDate, 'MMM d')})
                      </span>
                    )}
                    <ChevronDown className={`ml-1.5 h-3.5 w-3.5 text-gray-500 transition-transform duration-200 ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isDateRangeOpen && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                      {DATE_RANGES.map((range) => (
                        <React.Fragment key={range.value}>
                          <button
                            onClick={() => {
                              setSelectedRange(range);
                              if (range.value === 'select_day') {
                                setIsDatePickerOpen(true);
                              } else if (range.value === 'custom_range') {
                                setIsCustomRangePickerOpen(true);
                              } else {
                                setIsDateRangeOpen(false);
                              }
                            }}
                            className={`w-full text-left px-4 py-2 text-xs ${
                              selectedRange.value === range.value
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {range.label}
                            {range.value === 'select_day' && selectedDay && (
                              <span className="ml-2 text-gray-500">
                                ({format(selectedDay, 'MMM d, yyyy')})
                              </span>
                            )}
                            {range.value === 'custom_range' && customStartDate && customEndDate && (
                              <span className="ml-2 text-gray-500">
                                ({format(customStartDate, 'MMM d')} - {format(customEndDate, 'MMM d')})
                              </span>
                            )}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  
                  {isDatePickerOpen && (
                    <div className="absolute right-0 mt-1 p-4 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                      <input
                        type="date"
                        value={selectedDay ? format(selectedDay, 'yyyy-MM-dd') : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                          setSelectedDay(date);
                          setIsDatePickerOpen(false);
                          setIsDateRangeOpen(false);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  
                  {isCustomRangePickerOpen && (
                    <div className="absolute right-0 mt-1 p-4 bg-white rounded-md shadow-lg border border-gray-200 z-20 w-64">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                            onChange={(e) => {
                              const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                              setCustomStartDate(date);
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                            onChange={(e) => {
                              const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                              setCustomEndDate(date);
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex justify-end pt-2">
                          <button
                            onClick={() => {
                              setIsCustomRangePickerOpen(false);
                              setIsDateRangeOpen(false);
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">
                        {isLoadingEarnings ? 'Loading earnings data...' : 'Earnings report'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsEarningsExpanded(!isEarningsExpanded)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                    >
                      {isEarningsExpanded ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
                      {isEarningsExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Hours</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {isLoadingEarnings ? '...' : earningsData.totalHours.toFixed(1)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Earnings</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {isLoadingEarnings ? '...' : `$${earningsData.totalEarnings.toFixed(2)}`}
                    </p>
                  </div>
                </div>

                {/* Expandable Earnings Detail Section */}
                {isEarningsExpanded && (
                  <div className="mt-6 border-t pt-6">
                    {/* Flat Rate Entries */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Flat Rate Entries</h4>
                      {sortedBookingIds.length > 0 ? (
                        sortedBookingIds.map(bookingId => {
                          const entries = bookings[bookingId];
                          const totalHours = entries.reduce((sum, entry) => sum + entry.hours, 0);
                          const totalEarnings = entries.reduce((sum, entry) => sum + entry.earnings, 0);
                          const firstEntry = entries[0];
                          const mostRecentDate = entries.reduce((latest, entry) => {
                            const entryDate = new Date(entry.completed_at || entry.created_at);
                            return entryDate > latest ? entryDate : latest;
                          }, new Date(0));

                          return (
                            <div key={bookingId} className="border rounded-lg mb-4">
                              <div 
                                className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleBooking(bookingId)}
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    Booking #{firstEntry.booking_number} - {firstEntry.booking_title}
                                  </p>
                                  <div className="flex items-center space-x-3">
                                    <p className="text-xs text-gray-500">
                                      Total: {totalHours.toFixed(1)} hours, ${totalEarnings.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Last updated: {format(mostRecentDate, 'MMM d, yyyy')}
                                    </p>
                                  </div>
                                </div>
                                <ChevronDown className={`h-5 w-5 text-gray-400 transform transition-transform ${
                                  expandedBookings.has(bookingId) ? 'rotate-180' : ''
                                }`} />
                              </div>

                              {expandedBookings.has(bookingId) && (
                                <div className="border-t">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Item
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Date
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Hours
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Earnings
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {entries
                                        .sort((a, b) => {
                                          const dateA = new Date(a.completed_at || a.created_at);
                                          const dateB = new Date(b.completed_at || b.created_at);
                                          return dateB.getTime() - dateA.getTime(); // Newest first
                                        })
                                        .map((entry) => (
                                          <tr key={entry.id}>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <div className="text-sm text-gray-900">{entry.item_name}</div>
                                              {entry.item_description && (
                                                <div className="text-xs text-gray-500">{entry.item_description}</div>
                                              )}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {format(new Date(entry.completed_at || entry.created_at), 'MMM d, yyyy')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                              {entry.hours.toFixed(1)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                              ${entry.earnings.toFixed(2)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className={`px-2 py-1 text-xs rounded-full ${
                                                entry.process_status === 'PAID' 
                                                  ? 'bg-green-100 text-green-700' 
                                                  : 'bg-yellow-100 text-yellow-700'
                                              }`}>
                                                {entry.process_status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500 italic">No flat rate entries for this period</p>
                      )}
                    </div>

                    {/* Commission Entries */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Commission Entries</h4>
                      {sortedCommissionBookingIds.length > 0 ? (
                        sortedCommissionBookingIds.map(bookingId => {
                          const entries = commissionBookings[bookingId];
                          const totalEarnings = entries.reduce((sum, entry) => sum + entry.earnings, 0);
                          const firstEntry = entries[0];
                          const mostRecentDate = entries.reduce((latest, entry) => {
                            const entryDate = new Date(entry.completed_at || entry.created_at);
                            return entryDate > latest ? entryDate : latest;
                          }, new Date(0));

                          return (
                            <div key={`commission-${bookingId}`} className="border rounded-lg mb-4">
                              <div 
                                className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleBooking(`commission-${bookingId}`)}
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    Booking #{firstEntry.booking_number} - {firstEntry.booking_title}
                                  </p>
                                  <div className="flex items-center space-x-3">
                                    <p className="text-xs text-gray-500">
                                      Total: ${totalEarnings.toFixed(2)} commission
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Last updated: {format(mostRecentDate, 'MMM d, yyyy')}
                                    </p>
                                  </div>
                                </div>
                                <ChevronDown className={`h-5 w-5 text-gray-400 transform transition-transform ${
                                  expandedBookings.has(`commission-${bookingId}`) ? 'rotate-180' : ''
                                }`} />
                              </div>

                              {expandedBookings.has(`commission-${bookingId}`) && (
                                <div className="border-t">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Type
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Date
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Commission
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Earnings
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {entries
                                        .sort((a, b) => {
                                          const dateA = new Date(a.completed_at || a.created_at);
                                          const dateB = new Date(b.completed_at || b.created_at);
                                          return dateB.getTime() - dateA.getTime(); // Newest first
                                        })
                                        .map((entry) => (
                                          <tr key={entry.id}>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <div className="text-sm text-gray-900">{entry.item_name}</div>
                                              {entry.item_description && (
                                                <div className="text-xs text-gray-500">{entry.item_description}</div>
                                              )}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {format(new Date(entry.completed_at || entry.created_at), 'MMM d, yyyy')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                              Commission
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                              ${entry.earnings.toFixed(2)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className={`px-2 py-1 text-xs rounded-full ${
                                                entry.process_status === 'PAID' 
                                                  ? 'bg-green-100 text-green-700' 
                                                  : 'bg-yellow-100 text-yellow-700'
                                              }`}>
                                                {entry.process_status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500 italic">No commission entries for this period</p>
                      )}
                    </div>

                    {/* Timeclock Entries */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Timeclock Entries</h4>
                      {sortedTimeclockEntries.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Clock In
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Clock Out
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Hours
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Earnings
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {sortedTimeclockEntries.map((entry) => (
                              <tr key={entry.id}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {format(new Date(entry.clock_in || entry.created_at), 'MMM d, yyyy')}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {entry.clock_in ? format(new Date(entry.clock_in), 'h:mm a') : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {entry.hours.toFixed(1)}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  ${entry.earnings.toFixed(2)}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    entry.process_status === 'PAID' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {entry.process_status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No timeclock entries for this period</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 